import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from '@mui/material/IconButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  DataGridProps,
  GridColDef,
  GridRenderCellParams,
  GridSelectionModel,
  GridValueGetterParams,
  GridToolbarContainer,
} from '@mui/x-data-grid';

import { alpha } from '@mui/material/styles';

import StripedDataGrid from 'shared/components/StripedDataGrid';
import { ReviewRow, Section } from 'pages/SearchTermReview/types';

interface Props {
  rows: ReviewRow[];
  section: Section;
  reviewerName: string;
  savingKey: string | null;
  bulkSaving: boolean;
  // Google Ads "obfuscated customer id" — makes the deep links open the right account
  // for multi-account users. Empty = link still works but opens the last-used account.
  googleAdsOcid: string;
  // term_keys already pushed to Google Ads this session — marked "pushed" so a reviewer
  // sees the row is done (it's already excluded from the pushable set upstream).
  pushedKeys: Set<string>;
  onDecision: (row: ReviewRow, decision: 'approve' | 'deny') => void;
  onBulkDecision: (rows: ReviewRow[], decision: 'approve' | 'deny') => void;
}

// Deep link into the Google Ads UI at this row's ad group (keywords view — the
// triggering keywords are visible there).
function googleAdsUrl(row: ReviewRow, ocid: string): string {
  const params = new URLSearchParams({ campaignId: row.campaign_id, adGroupId: row.ad_group_id });
  if (ocid.trim() !== '') params.set('ocid', ocid.trim());
  return `https://ads.google.com/aw/keywords?${params.toString()}`;
}

const money = (v: number | null | undefined) =>
  v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const ratio = (v: number | null | undefined) => (v == null ? '—' : Number(v).toFixed(2));

const plainNum = (v: number | null | undefined) => (v == null ? '—' : String(v));

// Shows the value and, on hover, the full (untruncated) value — for cells that get
// clipped in the narrow columns.
function cellTip(text: string) {
  return (
    <Tooltip title={text}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
        {text}
      </span>
    </Tooltip>
  );
}

function numberColumn(
  field: string,
  headerName: string,
  width: number,
  fmt: (v: number | null | undefined) => string,
): GridColDef {
  return {
    field,
    headerName,
    width,
    type: 'number',
    renderCell: (p: GridRenderCellParams) => cellTip(fmt(p.value as number | null)),
  };
}

const analysisLabel = (r: ReviewRow) =>
  r.evaluated_by === 'llm' ? 'AI' : r.evaluated_by === 'fallback' ? 'Rules (AI failed)' : 'Rules';

// ---- Plural grouping (Add-Keyword tab only) --------------------------------
// A PHRASE keyword already matches singular/plural close variants in Google Ads, so
// recommending "cooling comforter" AND "cooling comforters" as separate keyword adds
// is redundant — and it buries genuinely new terms down the list. We fold plural
// variants of the same phrase, in the same ad group, into one canonical row; the
// reviewer approves the canonical (which covers the plurals) or expands to opt an
// individual variant in. Rows stay real ReviewRows underneath, so push/decisions/CSV
// are unaffected — this is purely how the tab is displayed.
function singularizeWord(w: string): string {
  if (w.length <= 3) return w; // leave short words alone (gas, is, us…)
  if (/(?:s|sh|ch|x|z)es$/.test(w)) return w.slice(0, -2); // boxes→box, glasses→glass
  if (/ies$/.test(w) && w.length > 4) return `${w.slice(0, -3)}y`; // babies→baby
  if (/s$/.test(w) && !/(?:ss|us|is)$/.test(w)) return w.slice(0, -1); // comforters→comforter (not mattress)
  return w;
}

// Order-preserving, word-by-word singular normalization; two terms that differ only by
// pluralization map to the same key.
function pluralKey(term: string): string {
  return term
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(singularizeWord)
    .join(' ');
}

interface PluralGroup {
  canonical: ReviewRow;
  variants: ReviewRow[]; // folded members (excludes canonical), spend-descending
  total: { spend: number; conversions: number; conversions_value: number };
}

// Keep the proven performer as the keyword to add: most conversions, then conv value,
// then spend, then the shortest text (usually the singular), then alphabetical.
function pickCanonical(members: ReviewRow[]): ReviewRow {
  return [...members].sort(
    (a, b) =>
      (b.conversions || 0) - (a.conversions || 0) ||
      (b.conversions_value || 0) - (a.conversions_value || 0) ||
      (b.spend || 0) - (a.spend || 0) ||
      a.search_term.length - b.search_term.length ||
      a.search_term.localeCompare(b.search_term),
  )[0];
}

// Fold plural variants into their canonical. Returns the rows the grid should show
// (canonicals + ungrouped singletons, in the incoming spend-descending order) and a
// lookup from a canonical's term_key to its group.
export function groupPlurals(rows: ReviewRow[]): { gridRows: ReviewRow[]; groups: Map<string, PluralGroup> } {
  // A keyword is ad-group-scoped, so only fold plurals sharing account + campaign + ad
  // group; the same phrase in another ad group is a genuinely separate add.
  const keyOf = (r: ReviewRow) => [r.account_id, r.campaign_id, r.ad_group_id, pluralKey(r.search_term)].join('\u0000');
  const buckets = new Map<string, ReviewRow[]>();
  for (const r of rows) {
    const k = keyOf(r);
    const b = buckets.get(k);
    if (b) b.push(r);
    else buckets.set(k, [r]);
  }
  const gridRows: ReviewRow[] = [];
  const groups = new Map<string, PluralGroup>();
  const seen = new Set<string>();
  for (const r of rows) {
    const k = keyOf(r);
    if (seen.has(k)) continue;
    seen.add(k);
    const members = buckets.get(k)!;
    if (members.length < 2) {
      gridRows.push(r);
      continue;
    }
    const canonical = pickCanonical(members);
    const variants = members.filter(m => m !== canonical).sort((a, b) => (b.spend || 0) - (a.spend || 0));
    gridRows.push(canonical); // anchored at the group's highest-spend position
    groups.set(canonical.term_key, {
      canonical,
      variants,
      total: members.reduce(
        (t, m) => ({
          spend: t.spend + (m.spend || 0),
          conversions: t.conversions + (m.conversions || 0),
          conversions_value: t.conversions_value + (m.conversions_value || 0),
        }),
        { spend: 0, conversions: 0, conversions_value: 0 },
      ),
    });
  }
  return { gridRows, groups };
}

// ---- CSV export ------------------------------------------------------------
// Each tab exports the shape it's used for: the Negatives / Add-Keyword tabs export
// the Google Ads Editor bulk-upload columns; the rest export a full review sheet.
function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(headers: string[], rows: (string | number | null | undefined)[][]): string {
  return [headers, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n');
}

type ExportBuild = (approved: ReviewRow[]) => { fileName: string; csv: string };

// A negative is ad-group-scoped only when the recommendation says so; otherwise it's
// campaign-level and the Ad Group cell is left blank (Google Ads Editor reads a blank
// ad group as a campaign negative).
const adGroupForNegative = (r: ReviewRow) => (/ad group/i.test(r.recommendation) ? r.ad_group_name : '');

// Compact "just the applicable changes" upload for negatives. Uses headers Google Ads
// Editor auto-recognizes and encodes "negative" in the match type value ("Negative
// Phrase"/"Negative Exact" are accepted Criterion/Match Type values), so the file
// imports with no manual column mapping. Blank Ad Group = campaign-level negative.
// Match type is per row: EXACT when a phrase negative would also block profitable
// queries (see the row's reasoning), PHRASE when safe.
const buildNegativesUpload: ExportBuild = approved => ({
  fileName: 'dosaze-negatives-upload.csv',
  csv: toCsv(
    ['Campaign', 'Ad Group', 'Keyword', 'Match Type'],
    // Cluster negatives upload the BRAND phrase (one negative covers the whole
    // unprofitable brand cluster) — everything else uploads the search term.
    approved.map(r => [
      r.campaign_name,
      adGroupForNegative(r),
      r.negative_keyword ?? r.search_term,
      r.negative_match_type === 'EXACT' ? 'Negative Exact' : 'Negative Phrase',
    ]),
  ),
});

// Compact upload for keyword adds — Google Ads Editor columns (keywords are always
// ad-group-scoped): Campaign, Ad Group, Keyword, Match Type.
const buildKeywordsUpload: ExportBuild = approved => ({
  fileName: 'dosaze-add-keywords-upload.csv',
  csv: toCsv(
    ['Campaign', 'Ad Group', 'Keyword', 'Match Type'],
    approved.map(r => [r.campaign_name, r.ad_group_name, r.search_term, 'Phrase']),
  ),
});

// Refine = tighten the triggering keyword to a different (stricter) match type than it
// currently is. Broad -> Phrase, Phrase -> Exact, Exact -> Phrase (always a change).
const tightenMatchType = (current: string | null): string => {
  switch ((current ?? '').toUpperCase()) {
    case 'BROAD':
      return 'Phrase';
    case 'PHRASE':
      return 'Exact';
    default:
      return 'Phrase';
  }
};

// Refine upload — re-add the triggering keyword(s) at the tightened match type (Google
// Ads Editor: adding the keyword with the new match type is how you change it in bulk).
// A row covers every keyword that matched the term in its ad group, so export one line
// per keyword, each tightened from its own current match type.
const buildRefineUpload: ExportBuild = approved => ({
  fileName: 'dosaze-refine-match-type-upload.csv',
  csv: toCsv(
    ['Campaign', 'Ad Group', 'Keyword', 'Match Type'],
    approved.flatMap(r => {
      const kws = (r.keywords ?? []).filter(k => (k.triggering_keyword ?? '').trim() !== '');
      if (kws.length === 0) {
        return (r.triggering_keyword ?? '').trim() === ''
          ? []
          : [[r.campaign_name, r.ad_group_name, r.triggering_keyword, tightenMatchType(r.match_type)]];
      }
      return kws.map(k => [r.campaign_name, r.ad_group_name, k.triggering_keyword, tightenMatchType(k.match_type)]);
    }),
  ),
});

// Full review sheet (every detail) for any tab.
function buildReview(section: Section): ExportBuild {
  return approved => ({
    fileName: `dosaze-${section}-review.csv`,
    csv: toCsv(
      [
        'Campaign', 'Ad Group', 'Search Term', 'Triggering Keyword', 'Match Type',
        'Spend', 'Clicks', 'Conversions', 'Conv Value', 'CPA', 'ROAS',
        'Analysis', 'Issue Category', 'Recommendation', 'Reasoning', 'Decision',
      ],
      approved.map(r => [
        r.campaign_name, r.ad_group_name, r.search_term,
        (r.keywords ?? []).length > 0
          ? r.keywords.map(k => `${k.triggering_keyword ?? ''}${k.match_type ? ` (${k.match_type})` : ''}`).join('; ')
          : r.triggering_keyword ?? '',
        r.match_type ?? '',
        r.spend ?? '', r.clicks ?? '', r.conversions ?? '', r.conversions_value ?? '',
        r.cpa ?? '', r.roas ?? '', analysisLabel(r), r.issue_category, r.recommendation, r.reasoning,
        r.decision?.decision ?? '',
      ]),
    ),
  });
}

// The export choices offered per tab. Negatives/Add-Keyword offer the compact upload
// first (the applicable changes), plus the full review sheet; other tabs only review.
function exportOptions(section: Section): { label: string; build: ExportBuild }[] {
  const review = { label: 'Full review CSV (all columns)', build: buildReview(section) };
  const negatives = { label: 'Negatives upload — Campaign, Ad Group, Keyword, Match Type', build: buildNegativesUpload };
  if (section === 'negatives') return [negatives, review];
  if (section === 'add-keyword')
    return [{ label: 'Keywords upload — Campaign, Ad Group, Keyword, Match Type', build: buildKeywordsUpload }, review];
  if (section === 'refine-match-type')
    return [{ label: 'Refine upload — keyword at tightened match type', build: buildRefineUpload }, review];
  return [review];
}

function downloadCsv(fileName: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

// Export dropdown — approved rows only, with tab-appropriate formats. A STABLE
// component (fed via DataGrid `componentsProps.toolbar`) so it isn't remounted on
// every parent render — otherwise the open Export menu would close whenever the rows
// update (e.g. during live AI scoring).
function ExportToolbar({ approvedRows, section }: { approvedRows: ReviewRow[]; section: Section }) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const options = exportOptions(section);
  return (
    <GridToolbarContainer>
      <Button
        size="small"
        startIcon={<SaveAltIcon />}
        endIcon={<ArrowDropDownIcon />}
        disabled={approvedRows.length === 0}
        onClick={e => setAnchor(e.currentTarget)}
      >
        Export
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {options.map(opt => (
          <MenuItem
            key={opt.label}
            onClick={() => {
              const { fileName, csv } = opt.build(approvedRows);
              downloadCsv(fileName, csv);
              setAnchor(null);
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
      <Box sx={{ ml: 1, color: 'text.secondary', fontSize: 13, alignSelf: 'center' }}>
        {approvedRows.length} approved row(s)
      </Box>
    </GridToolbarContainer>
  );
}

export default function SearchTermTable({
  rows,
  section,
  reviewerName,
  savingKey,
  bulkSaving,
  googleAdsOcid,
  pushedKeys,
  onDecision,
  onBulkDecision,
}: Props) {
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  // DataGrid v5 treats pageSize as a controlled prop — without state + onPageSizeChange
  // the rows-per-page selector silently does nothing.
  const [pageSize, setPageSize] = useState(25);
  // Which rows have their reasoning bullets expanded.
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set());
  // Which plural groups (Add-Keyword tab) are expanded to show their variants.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const noReviewer = reviewerName.trim() === '';
  // A pushed change is final: a new decision on it would hide the push and make it
  // pushable again.
  const isPushed = (r: ReviewRow) => pushedKeys.has(r.term_key) || !!r.decision?.pushed;
  // A row being AI-scored has no judged change to decide on yet, and the scoring response
  // replaces its decision, so a decision saved meanwhile would be lost.
  const isLocked = (r: ReviewRow) => isPushed(r) || r.ai_pending;

  // Pushed rows are already in Google Ads, so they stay out of the upload CSVs.
  const approvedRows = rows.filter(r => r.decision?.decision === 'approve' && !isPushed(r));

  // Add-Keyword folds plural variants into one canonical row; every other tab shows its
  // rows as-is. `groups` maps a canonical row's term_key to its folded variants.
  const { gridRows, groups } = React.useMemo(
    () =>
      section === 'add-keyword'
        ? groupPlurals(rows)
        : { gridRows: rows, groups: new Map<string, PluralGroup>() },
    [rows, section],
  );

  const doBulk = (decision: 'approve' | 'deny') => {
    const selected = rows.filter(r => selectionModel.includes(r.term_key) && !isLocked(r));
    if (selected.length === 0) return;
    onBulkDecision(selected, decision);
    setSelectionModel([]);
  };

  const columns: GridColDef[] = [
    {
      field: 'search_term',
      headerName: 'Search Term',
      flex: 1.2,
      minWidth: 150,
      renderCell: (p: GridRenderCellParams) => {
        const row = p.row as ReviewRow;
        const multiPlacement = (row.term_placements ?? 1) > 1;
        const group = groups.get(row.term_key);
        const groupExpanded = expandedGroups.has(row.term_key);
        const toggleGroup = () =>
          setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(row.term_key)) next.delete(row.term_key);
            else next.add(row.term_key);
            return next;
          });
        return (
          <Stack spacing={0.3} sx={{ py: 0.5, width: '100%' }}>
            <span style={{ fontWeight: 600 }}>{row.search_term}</span>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
              {isPushed(row) && (
                <Tooltip title="Already pushed to Google Ads, so it won't be included in another push.">
                  <Chip size="small" color="success" label="pushed ✓" />
                </Tooltip>
              )}
              {group && (
                <Tooltip
                  title={`${group.variants.length} plural variant(s) of this phrase were found in this ad group. Adding the PHRASE keyword “${row.search_term}” already matches the plural queries, so they're folded here instead of listed as separate adds. Expand to add one individually.`}
                >
                  <Chip size="small" color="primary" variant="outlined" label={`+${group.variants.length} plural${group.variants.length > 1 ? 's' : ''}`} />
                </Tooltip>
              )}
              {row.competitor_flag && <Chip size="small" color="warning" label="comp" />}
              {row.own_brand_flag && <Chip size="small" color="info" label="brand" />}
              {row.waste_flag && <Chip size="small" color="error" label="waste" />}
              {row.already_negated && (
                <Tooltip
                  title={`Already blocked by the existing ${row.already_negated.match_type.toLowerCase()}-match negative “${row.already_negated.text}” at ${row.already_negated.level.replace('_', ' ')} level (live Google Ads check).`}
                >
                  <Chip size="small" color="success" variant="outlined" label="already negated" />
                </Tooltip>
              )}
              {row.already_added && (
                <Tooltip title="This search term is already added as a keyword in Google Ads (status ADDED) — shown for visibility only; no action is recommended.">
                  <Chip size="small" color="info" variant="outlined" label="already added" />
                </Tooltip>
              )}
              {multiPlacement && (
                <Tooltip
                  title={`This term also matched in ${row.term_placements - 1} other ad group(s). Term total across all of them: ${money(row.term_spend)} spend, ${plainNum(row.term_conversions)} conv, ROAS ${ratio(row.term_roas)}. The analysis judges the whole term, not this row alone.`}
                >
                  <Chip size="small" variant="outlined" label={`×${row.term_placements} ad groups`} />
                </Tooltip>
              )}
            </Stack>
            {group && (
              <>
                <Button
                  size="small"
                  onClick={toggleGroup}
                  sx={{ alignSelf: 'flex-start', minWidth: 0, p: 0, fontSize: 12 }}
                >
                  {groupExpanded ? 'Hide plural variants ▴' : `Show ${group.variants.length} plural variant${group.variants.length > 1 ? 's' : ''} ▾`}
                </Button>
                {groupExpanded && (
                  <Stack
                    spacing={0.6}
                    sx={theme => ({
                      mt: 0.3,
                      pl: 1,
                      borderLeft: `2px solid ${theme.palette.divider}`,
                    })}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
                      Group total: {money(group.total.spend)} spend · {plainNum(group.total.conversions)} conv. Approving
                      the row above adds only “{row.search_term}”, which phrase-matches these plurals. Add one below only
                      if you want it as its own keyword too.
                    </Typography>
                    {group.variants.map(v => {
                      const decided = v.decision?.decision;
                      const vDisabled = savingKey === v.term_key || noReviewer || isLocked(v);
                      return (
                        <Stack key={v.term_key} spacing={0.2}>
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.3 }}>
                            <span style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>{v.search_term}</span>
                            {isPushed(v) && <Chip size="small" color="success" label="pushed ✓" />}
                          </Stack>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.3 }}>
                            <Typography variant="caption" color="text.secondary">
                              {money(v.spend)} · {plainNum(v.conversions)} conv · ROAS {ratio(v.roas)}
                            </Typography>
                            <Tooltip title={`Also add “${v.search_term}” as its own keyword`}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Also add “${v.search_term}”`}
                                  disabled={vDisabled}
                                  color={decided === 'approve' ? 'success' : 'default'}
                                  onClick={() => onDecision(v, 'approve')}
                                >
                                  <CheckCircleIcon fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={`Don't add “${v.search_term}”`}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Don't add “${v.search_term}”`}
                                  disabled={vDisabled}
                                  color={decided === 'deny' ? 'error' : 'default'}
                                  onClick={() => onDecision(v, 'deny')}
                                >
                                  <CancelIcon fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'triggering_keyword',
      headerName: 'Trigger KWs',
      flex: 0.8,
      minWidth: 120,
      // Sort/filter/export on the full keyword list, not just the top one.
      valueGetter: (p: GridValueGetterParams) => {
        const row = p.row as ReviewRow;
        const kws = row.keywords ?? [];
        if (kws.length === 0) return `${row.triggering_keyword ?? '—'}${row.match_type ? ` (${row.match_type})` : ''}`;
        return kws.map(k => `${k.triggering_keyword ?? '—'}${k.match_type ? ` (${k.match_type})` : ''}`).join('; ');
      },
      renderCell: (p: GridRenderCellParams) => {
        const row = p.row as ReviewRow;
        const kws = row.keywords ?? [];
        if (kws.length <= 1) return cellTip(String(p.value ?? '—'));
        // All keywords that matched this term in this ad group, with each one's own
        // numbers — the reason a negative here needs the whole row's context.
        const fam = row.query_family;
        const breakdown = (
          <Stack spacing={0.5}>
            <span style={{ fontWeight: 600 }}>{kws.length} keywords matched this term here:</span>
            {kws.map((k, idx) => (
              <span key={idx}>
                {k.triggering_keyword ?? '—'}
                {k.match_type ? ` (${k.match_type})` : ''} — {money(k.spend)}, {k.conversions} conv
                {k.roas != null ? `, ROAS ${ratio(k.roas)}` : ''}
              </span>
            ))}
            {fam && fam.terms > 1 && (
              <span>
                Query family (near-duplicate variants here): {fam.terms} terms, {money(fam.spend)},{' '}
                {fam.conversions} conv, ROAS {ratio(fam.roas)}
                {fam.driving.length > 0 ? ` — driven by ${fam.driving.map(d => `“${d.search_term}”`).slice(0, 2).join(', ')}` : ''}
              </span>
            )}
          </Stack>
        );
        return (
          <Tooltip title={breakdown}>
            <Stack spacing={0.3} sx={{ py: 0.5, overflow: 'hidden' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {kws[0].triggering_keyword ?? '—'}
                {kws[0].match_type ? ` (${kws[0].match_type})` : ''}
              </span>
              <Chip size="small" variant="outlined" label={`+${kws.length - 1} more KW`} sx={{ alignSelf: 'flex-start' }} />
            </Stack>
          </Tooltip>
        );
      },
    },
    { field: 'ad_group_name', headerName: 'Ad Group', flex: 0.8, minWidth: 100 },
    { field: 'campaign_name', headerName: 'Campaign', flex: 0.9, minWidth: 120 },
    {
      field: 'google_ads_link',
      headerName: '',
      width: 44,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: (p: GridRenderCellParams) => {
        const row = p.row as ReviewRow;
        return (
          <Tooltip title="Open this ad group's keywords in Google Ads">
            <IconButton
              size="small"
              component="a"
              href={googleAdsUrl(row, googleAdsOcid)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    numberColumn('spend', 'Spend', 78, money),
    numberColumn('clicks', 'Clk', 56, plainNum),
    numberColumn('conversions', 'Conv', 60, plainNum),
    numberColumn('conversions_value', 'Conv $', 84, money),
    numberColumn('cpa', 'CPA', 64, money),
    numberColumn('roas', 'ROAS', 58, ratio),
    {
      field: 'evaluated_by',
      headerName: 'By',
      width: 74,
      // Export a readable label, not the raw enum (also what sort/filter uses).
      valueGetter: (p: GridValueGetterParams) => {
        const row = p.row as ReviewRow;
        if (row.ai_pending) return 'AI scoring';
        if (row.evaluated_by === 'llm') return 'AI';
        if (row.evaluated_by === 'fallback') return 'Rules (AI failed)';
        return 'Rules';
      },
      renderCell: (p: GridRenderCellParams) => {
        const row = p.row as ReviewRow;
        if (row.ai_pending) return <Chip size="small" color="primary" variant="outlined" label="AI…" />;
        if (row.evaluated_by === 'llm') return <Chip size="small" color="primary" label="AI" />;
        if (row.evaluated_by === 'fallback')
          return (
            <Tooltip title="Rules (AI failed)">
              <Chip size="small" color="warning" variant="outlined" label="Rules*" />
            </Tooltip>
          );
        return <Chip size="small" variant="outlined" label="Rules" />;
      },
    },
    {
      field: 'issue_category',
      headerName: 'Issue',
      width: 108,
      renderCell: (p: GridRenderCellParams) => {
        const c = (p.row as ReviewRow).issue_category;
        return (
          <Tooltip title={c}>
            <Chip size="small" variant="outlined" label={c} sx={{ maxWidth: '100%' }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'recommendation',
      headerName: 'Recommend',
      width: 126,
      renderCell: (p: GridRenderCellParams) => {
        const row = p.row as ReviewRow;
        if (!row.negative_match_type) return cellTip(row.recommendation);
        const exact = row.negative_match_type === 'EXACT';
        const blockers = row.phrase_blockers ?? [];
        return (
          <Tooltip
            title={
              exact
                ? `Exact-match negative: a phrase negative would also block profitable queries${
                    blockers.length ? ` like ${blockers.map(b => `“${b.search_term}”`).slice(0, 3).join(', ')}` : ''
                  }.`
                : 'Phrase negative — no profitable queries in this campaign contain this term.'
            }
          >
            <Stack spacing={0.3} sx={{ py: 0.5 }}>
              <span>{row.recommendation}</span>
              <Chip
                size="small"
                variant="outlined"
                color={exact ? 'warning' : 'default'}
                label={exact ? 'exact' : 'phrase'}
                sx={{ alignSelf: 'flex-start' }}
              />
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: 'reasoning',
      headerName: 'Reasoning & decision',
      flex: 1.4,
      minWidth: 240,
      renderCell: (p: GridRenderCellParams) => {
        const row = p.row as ReviewRow;
        const decided = row.decision?.decision;
        const disabled = savingKey === row.term_key || noReviewer || isLocked(row);
        return (
          <Stack spacing={0.75} sx={{ width: '100%', py: 0.5 }}>
            <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                startIcon={<CheckCircleIcon />}
                onClick={() => onDecision(row, 'approve')}
                sx={theme => ({
                  flex: 1,
                  color: theme.palette.success.main,
                  fontWeight: decided === 'approve' ? 700 : 500,
                  borderColor:
                    decided === 'approve' ? theme.palette.success.main : alpha(theme.palette.success.main, 0.5),
                  bgcolor: decided === 'approve' ? alpha(theme.palette.success.main, 0.14) : 'transparent',
                  '&:hover': {
                    borderColor: theme.palette.success.main,
                    bgcolor: alpha(theme.palette.success.main, decided === 'approve' ? 0.2 : 0.06),
                  },
                })}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                startIcon={<CancelIcon />}
                onClick={() => onDecision(row, 'deny')}
                sx={theme => ({
                  flex: 1,
                  color: theme.palette.error.main,
                  fontWeight: decided === 'deny' ? 700 : 500,
                  borderColor:
                    decided === 'deny' ? theme.palette.error.main : alpha(theme.palette.error.main, 0.5),
                  bgcolor: decided === 'deny' ? alpha(theme.palette.error.main, 0.14) : 'transparent',
                  '&:hover': {
                    borderColor: theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, decided === 'deny' ? 0.2 : 0.06),
                  },
                })}
              >
                Deny
              </Button>
            </Stack>
            {(() => {
              const main = row.reasoning_main || row.reasoning;
              const details = row.reasoning_details ?? [];
              const expanded = expandedReasons.has(row.term_key);
              const toggle = () =>
                setExpandedReasons(prev => {
                  const next = new Set(prev);
                  if (next.has(row.term_key)) next.delete(row.term_key);
                  else next.add(row.term_key);
                  return next;
                });
              return (
                <Stack spacing={0.4}>
                  <span style={{ whiteSpace: 'normal', lineHeight: 1.3, fontWeight: 500 }}>{main}</span>
                  {details.length > 0 && (
                    <>
                      {expanded && (
                        <ul style={{ margin: 0, paddingLeft: 18, whiteSpace: 'normal', lineHeight: 1.35 }}>
                          {details.map((d, i) => (
                            <li key={i} style={{ marginBottom: 2 }}>
                              {d}
                            </li>
                          ))}
                        </ul>
                      )}
                      <Button size="small" onClick={toggle} sx={{ alignSelf: 'flex-start', minWidth: 0, p: 0, fontSize: 12 }}>
                        {expanded ? 'Less ▴' : `More (${details.length}) ▾`}
                      </Button>
                    </>
                  )}
                </Stack>
              );
            })()}
          </Stack>
        );
      },
    },
  ];

  const components: DataGridProps['components'] = { Toolbar: ExportToolbar };
  const componentsProps: DataGridProps['componentsProps'] = { toolbar: { approvedRows, section } };

  // Totals over the rows currently shown (this tab + active filters; on Add-Keyword the
  // grid rows are the plural-folded canonicals). CPA/ROAS are blended from the totals,
  // not averaged across rows.
  const totalSpend = gridRows.reduce((a, r) => a + (r.spend || 0), 0);
  const totalClicks = gridRows.reduce((a, r) => a + (r.clicks || 0), 0);
  const totalConv = gridRows.reduce((a, r) => a + (r.conversions || 0), 0);
  const totalValue = gridRows.reduce((a, r) => a + (r.conversions_value || 0), 0);
  const blendedCpa = totalConv > 0 ? totalSpend / totalConv : null;
  const blendedRoas = totalSpend > 0 ? totalValue / totalSpend : null;

  return (
    <>
      {selectionModel.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectionModel.length} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={bulkSaving || noReviewer}
            onClick={() => doBulk('approve')}
          >
            Approve selected
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            disabled={bulkSaving || noReviewer}
            onClick={() => doBulk('deny')}
          >
            Deny selected
          </Button>
          {noReviewer && (
            <Typography variant="caption" color="error">
              Enter your name (top right) to approve/deny
            </Typography>
          )}
        </Stack>
      )}
      <StripedDataGrid
        autoHeight
        rows={gridRows}
        columns={columns}
        getRowId={row => (row as ReviewRow).term_key}
        getRowClassName={params => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        getRowHeight={() => 'auto'}
        components={components}
        componentsProps={componentsProps}
        checkboxSelection
        selectionModel={selectionModel}
        onSelectionModelChange={m => setSelectionModel(m)}
        disableSelectionOnClick
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[25, 50, 100]}
        sx={{ '& .MuiDataGrid-cell': { py: 0.75, alignItems: 'flex-start' } }}
      />
      <Stack
        direction="row"
        spacing={3}
        flexWrap="wrap"
        sx={{ mt: 1, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1, rowGap: 0.5 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Totals ({gridRows.length} rows)
        </Typography>
        <Typography variant="body2">Spend {money(totalSpend)}</Typography>
        <Typography variant="body2">Clicks {totalClicks.toLocaleString()}</Typography>
        <Typography variant="body2">Conv {Number(totalConv.toFixed(2)).toLocaleString()}</Typography>
        <Typography variant="body2">Conv $ {money(totalValue)}</Typography>
        <Typography variant="body2">CPA {money(blendedCpa)}</Typography>
        <Typography variant="body2">ROAS {ratio(blendedRoas)}</Typography>
      </Stack>
    </>
  );
}
